/**
 * Pure helpers for the main-thread audio OUTPUT wiring (Goal 5) — the small
 * decisions around standing up the WebAudio graph, lifted out of main.ts so the
 * format/init contract is verifiable headlessly (test/audiosetup.test.mjs)
 * while the irreducibly-browser glue (new AudioContext, audioWorklet.addModule,
 * new AudioWorkletNode, .connect, .resume) stays in main.ts as the one manual
 * in-browser check (docs/HANDOFF.md §11).
 *
 * What lives here:
 *   - the core's fixed output format constants (48000 Hz, 2 channels — the HLE
 *     mixer's interleaved S16 stereo; see core/emulator.h, bindings/audiodrain.ts);
 *   - {@link audioContextOptions}: we REQUEST a 48000 Hz context so the context
 *     rate matches the core and no resampling is needed. A browser may ignore the
 *     hint and pick its hardware rate; {@link rateMatchesCore} lets the caller
 *     detect+log that mismatch (resampling would be a later iteration);
 *   - {@link buildWorkletInit}: the exact `{ type:"init", audioRing, channels,
 *     muted, gain }` message the worklet's port expects (mirrors audio-worklet.ts's
 *     WorkletInit), so the producer (cpu.worker) and consumer (worklet) agree on
 *     channel count from one source, and the worklet starts at the persisted
 *     boot mute + volume preference;
 *   - {@link buildSetMuted}: the runtime mute message forwarded to the worklet
 *     node on a settings toggle (same shape the audio.worker reducer consumes);
 *   - {@link buildSetGain}: the runtime VOLUME message forwarded to the worklet
 *     node on a settings volume change (settings.audioGain).
 *
 * §1.6: pure DSP/output wiring — no keys, no crypto, no game content.
 */
import type { MainToAudioMessage } from "./protocol";

/** The name the AudioWorkletProcessor registers under and the host instantiates
 *  via `new AudioWorkletNode(ctx, PROCESSOR_NAME, ...)`. Defined here (a pure,
 *  side-effect-free module) so main.ts can name the node WITHOUT importing the
 *  worklet module — whose top-level `registerProcessor(...)` would throw in the
 *  main thread (registerProcessor only exists in the AudioWorkletGlobalScope). */
export const PROCESSOR_NAME = "voland-audio";

/** The core HLE mixer's fixed sample rate (Hz). */
export const CORE_SAMPLE_RATE = 48000;

/** The core HLE mixer's fixed channel count (interleaved S16 stereo). */
export const CORE_CHANNELS = 2;

/**
 * Options to pass to `new AudioContext(...)`. We request the core's rate so the
 * render graph runs at 48000 Hz and the S16 PCM needs no resampling. `latencyHint
 * "interactive"` keeps the output buffer small for an emulator's responsiveness.
 */
export function audioContextOptions(): { sampleRate: number; latencyHint: "interactive" } {
  return { sampleRate: CORE_SAMPLE_RATE, latencyHint: "interactive" };
}

/** True iff a realised AudioContext rate matches the core (no resampling needed). */
export function rateMatchesCore(contextSampleRate: number): boolean {
  return contextSampleRate === CORE_SAMPLE_RATE;
}

/** Unity gain (no attenuation/amplification): the default boot volume when no
 *  preference is supplied. Mirrors audioconvert.UNITY_GAIN / settings.UNITY_AUDIO_GAIN. */
export const DEFAULT_GAIN = 1;

/** The worklet init message: the SAB ring, the channel count, and the boot mute +
 *  volume preferences. Mirrors WorkletInit in workers/audio-worklet.ts. */
export interface WorkletInitMessage {
  readonly type: "init";
  readonly audioRing: SharedArrayBuffer;
  readonly channels: number;
  readonly muted: boolean;
  readonly gain: number;
}

/**
 * Build the init message handed to the worklet node's port: hands it the SAME
 * SharedArrayBuffer the cpu.worker producer holds, the core channel count, the
 * boot mute flag (settings.muteAudio), and the boot volume (settings.audioGain;
 * defaults to {@link DEFAULT_GAIN} unity). The worklet clamps the gain in its DSP.
 */
export function buildWorkletInit(
  audioRing: SharedArrayBuffer,
  muted: boolean,
  gain: number = DEFAULT_GAIN,
): WorkletInitMessage {
  return { type: "init", audioRing, channels: CORE_CHANNELS, muted, gain };
}

/** Build the runtime mute message forwarded to the worklet node (and the audio
 *  worker) when the settings toggle flips. Same shape both consumers reduce. */
export function buildSetMuted(muted: boolean): Extract<MainToAudioMessage, { type: "setMuted" }> {
  return { type: "setMuted", muted };
}

/** Build the runtime volume message forwarded to the worklet node when the
 *  settings volume (settings.audioGain) changes. The worklet folds + clamps the
 *  gain into its S16->Float32 convert. */
export function buildSetGain(gain: number): Extract<MainToAudioMessage, { type: "setGain" }> {
  return { type: "setGain", gain };
}

/**
 * Resolve the URL `audioWorklet.addModule` should fetch for the processor module.
 *
 *   - In a PRODUCTION build the worklet is emitted (by the dedicated Rollup entry
 *     in vite.config.ts) under the fixed, un-hashed name {@link AUDIO_WORKLET_FILE}
 *     beneath the deployment base, so we resolve `<baseUrl><AUDIO_WORKLET_FILE>`.
 *   - In DEV there is no build; Vite's dev server transpiles the .ts source on
 *     request, so we point at the source module path {@link AUDIO_WORKLET_DEV_SRC}.
 *
 * Kept pure (URL string assembly only) and tested against both modes; the actual
 * addModule/AudioContext glue stays in main.ts (the manual in-browser check).
 */
export const AUDIO_WORKLET_FILE = "assets/audio-worklet.js";
export const AUDIO_WORKLET_DEV_SRC = "/workers/audio-worklet.ts";

export function workletModuleUrl(dev: boolean, baseUrl: string): string {
  if (dev) return AUDIO_WORKLET_DEV_SRC;
  // baseUrl is Vite's BASE_URL (e.g. "/" or "/voland/"); join without doubling
  // the slash so the result is a single well-formed path.
  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${base}${AUDIO_WORKLET_FILE}`;
}
